import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { usePackageSeatmap } from '../PackageSeatmapProvider'

import { placePackageIntoCart } from '../../../utilities/api'
import { storeCartSession } from '../../../utilities/cartSessionTracking'
import {
  formatCurrency,
  formatShortDate,
  getStartDateFormatter,
  getSinglarOrPluralWord,
  getTimezoneDate,
  sort,
  timezones,
} from '../../../utilities/helpers'
import {
  packageAddedFeeLines,
  packageCustomFeeLines,
  previewPackageCharge,
} from '../../../utilities/packagePrice'
import {
  resolveBrandLogo,
  resolvePrimaryColor,
} from '../../PurchaseFlow/utils/branding'

import Form from 'react-bootstrap/Form'

import { Spinner } from '../../LoadingContainer/Spinner'

import logoWhite from '../../../assets/logo.svg'

import './packageDetails.scss'

const NAVY = '#051b35'

const isGAOnlyPackage = (eventPackage) =>
  Boolean(eventPackage?.seatmap?.ga_only)

const getBestGASection = (eventPackage) => {
  const packageTierPrice = eventPackage?.pricingTiers?.[0]?.price

  if (
    Array.isArray(eventPackage?.gaSections) &&
    eventPackage.gaSections.length
  ) {
    return (
      eventPackage.gaSections
        .filter((section) => (section?.availableCount || 0) > 0)
        .map((section) => ({
          GA: true,
          sectionId: section.sectionId,
          sectionName: section.sectionName,
          rowId: section.rowId,
          price: packageTierPrice ?? section.price,
          availableCount: Number(section.availableCount) || 0,
        }))
        .sort((a, b) => b.availableCount - a.availableCount)[0] || null
    )
  }

  const availableTickets = (eventPackage?.package_tickets || []).filter(
    (ticket) => ticket.GA && ticket.on_sale_status === 'available'
  )

  if (!availableTickets.length) return null

  const bySection = {}
  availableTickets.forEach((ticket) => {
    const { sectionId } = ticket
    if (!bySection[sectionId]) {
      bySection[sectionId] = {
        GA: true,
        sectionId,
        sectionName: ticket.sectionName,
        rowId: ticket.rowId,
        price: packageTierPrice ?? ticket.price,
        availableCount: 0,
      }
    }
    bySection[sectionId].availableCount += 1
  })

  return Object.values(bySection).sort(
    (a, b) => b.availableCount - a.availableCount
  )[0]
}

function adjustHex(hex, amount) {
  const cleaned = String(hex || NAVY).replace('#', '')
  const full =
    cleaned.length === 3
      ? cleaned
          .split('')
          .map((c) => c + c)
          .join('')
      : cleaned
  const n = parseInt(full, 16)
  if (Number.isNaN(n)) return hex || NAVY
  const adj = (c) => Math.min(255, Math.max(0, Math.round(c + amount)))
  const r = adj((n >> 16) & 255)
  const g = adj((n >> 8) & 255)
  const b = adj(n & 255)
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`
}

function formatFromPrice(amount) {
  const n = Number(amount) || 0
  if (n % 1 === 0) {
    return `From $${n.toLocaleString('en-US')}`
  }
  return `From ${formatCurrency(n)}`
}

function BackIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  )
}

export default function PackageDetails({ eventPackage }) {
  const { showSeatmap, setSeatedError } = usePackageSeatmap()
  const navigate = useNavigate()
  const heroRef = useRef(null)
  const [heroH, setHeroH] = useState(0)
  const [vw, setVw] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1440
  )

  const gaOnly = isGAOnlyPackage(eventPackage)
  const gaSection = useMemo(
    () => (gaOnly ? getBestGASection(eventPackage) : null),
    [gaOnly, eventPackage]
  )

  const minQuantity = eventPackage?.minQuantity || 1
  const maxQuantity = Math.min(
    eventPackage?.maxQuantity || 100,
    gaSection?.availableCount || 0
  )

  const quantityOptions = useMemo(() => {
    if (!gaOnly || maxQuantity < minQuantity) return []
    const options = []
    for (let count = minQuantity; count <= maxQuantity; count++) {
      options.push(count)
    }
    return options
  }, [gaOnly, minQuantity, maxQuantity])

  const [quantity, setQuantity] = useState(minQuantity)
  const [checkingAvailability, setCheckingAvailability] = useState(false)

  const selectedQuantity =
    quantityOptions.length === 0
      ? 0
      : quantityOptions.includes(quantity)
        ? quantity
        : quantityOptions[0]

  useEffect(() => {
    const onResize = () => setVw(window.innerWidth)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    const el = heroRef.current
    if (!el) return undefined
    const update = () => setHeroH(el.offsetHeight)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [eventPackage?.uuid, vw])

  const mobile = vw < 900
  const organization = eventPackage?.organization
  const primaryColor = resolvePrimaryColor(null, organization)
  const logoSrc =
    resolveBrandLogo(null, organization) || eventPackage?.image?.url || null
  const timezone =
    eventPackage?.timezone || timezones(eventPackage?.venue?.timezone)
  const events = sort(eventPackage?.events || [])
  const gameCount = events.length
  const unitPrice = eventPackage?.pricingTiers?.[0]?.price || 0
  const displayPrice = gaOnly ? unitPrice * (selectedQuantity || 1) : unitPrice
  const priceLabel = formatFromPrice(displayPrice)
  const addedFees = useMemo(() => {
    const seats = gaOnly ? Math.max(selectedQuantity || 1, 1) : 1
    const charge = previewPackageCharge({
      ticketSubtotal: unitPrice * seats,
      seatCount: seats,
      seatFees: eventPackage?.websiteSeatFees,
      customFees: packageCustomFeeLines(eventPackage),
    })
    return packageAddedFeeLines(charge)
  }, [eventPackage, gaOnly, selectedQuantity, unitPrice])

  const seasonLabel = useMemo(() => {
    if (eventPackage?.season?.name) return eventPackage.season.name
    const start = getTimezoneDate(
      eventPackage?.start || events[0]?.start,
      timezone
    )
    if (start) return `${start.format('YYYY')} season`
    return 'Season package'
  }, [eventPackage?.season?.name, eventPackage?.start, events, timezone])

  const dateRange = useMemo(() => {
    if (!events.length) return ''
    const first = getTimezoneDate(events[0]?.start, timezone)
    const last = getTimezoneDate(events[events.length - 1]?.start, timezone)
    if (!first || !last) return ''
    if (first.isSame(last, 'day')) return first.format('MMM D')
    if (first.isSame(last, 'year')) {
      return `${first.format('MMM D')} – ${last.format('MMM D')}`
    }
    return `${first.format('MMM D, YYYY')} – ${last.format('MMM D, YYYY')}`
  }, [events, timezone])

  const badges = useMemo(() => {
    const items = []
    if (eventPackage?.venue?.name) items.push(eventPackage.venue.name)
    if (gameCount > 0) {
      items.push(
        gaOnly
          ? `${gameCount} ${getSinglarOrPluralWord(gameCount, 'game')}`
          : `Same seat, ${gameCount} ${getSinglarOrPluralWord(gameCount, 'game')}`
      )
    }
    items.push('Transfer any game')
    return items
  }, [eventPackage?.venue?.name, gameCount, gaOnly])

  const gameRows = useMemo(() => {
    return events.map((event) => {
      const start = getTimezoneDate(event?.start, timezone)
      const venueName = event?.venue?.name || eventPackage?.venue?.name || ''
      const when = formatShortDate(start, getStartDateFormatter(event))
      return {
        key: event.uuid || event.shortCode || event.name,
        mon: formatShortDate(start, 'monthOnly') || '',
        day: String(formatShortDate(start, 'dayOnly') || '').padStart(2, '0'),
        title: event.name || 'Event',
        sub: [when, venueName].filter(Boolean).join(' · '),
      }
    })
  }, [events, timezone, eventPackage?.venue?.name])

  const handleGoBack = () => {
    const orgSlug = organization?.slug
    const venueSlug = eventPackage?.venue?.slug
    if (orgSlug) navigate(`/${orgSlug}`)
    else if (venueSlug) navigate(`/venue/${venueSlug}`)
    else if (window.history.length > 1) navigate(-1)
    else navigate('/browse')
  }

  const checkoutGA = () => {
    if (!gaSection || selectedQuantity < minQuantity) return

    setCheckingAvailability(true)
    const data = {
      packageTickets: [
        {
          GA: true,
          quantity: selectedQuantity,
          sectionId: gaSection.sectionId,
          sectionName: gaSection.sectionName,
          rowId: gaSection.rowId,
          price: gaSection.price,
        },
      ],
      eventPackageId: eventPackage.id,
    }

    placePackageIntoCart(data)
      .then((res) => {
        storeCartSession({
          cartId: res.data.cartId,
          sessionId: res.data.sessionId,
        })
        sessionStorage.setItem(
          'cart',
          JSON.stringify({ cartId: res.data.cartId })
        )
        navigate(`/checkout`)
      })
      .catch((err) => {
        if (err.response) {
          setSeatedError({
            status: err.response.status,
            title: 'Selected tickets not available',
            message: err.response.data.error.message || 'An error occurred',
            buttonText: 'Got it',
          })
        } else {
          setSeatedError({
            status: 500,
            title: 'Selected tickets not available',
            message: 'Network error occurred',
            buttonText: 'Got it',
          })
        }
      })
      .finally(() => {
        setCheckingAvailability(false)
      })
  }

  const isSoldOut = gaOnly && (!gaSection || quantityOptions.length === 0)

  const primaryCta = () => {
    if (gaOnly) {
      return (
        <button
          type="button"
          className="package-purchase__cta"
          disabled={isSoldOut || checkingAvailability || !selectedQuantity}
          onClick={checkoutGA}
        >
          {checkingAvailability ? (
            <Spinner />
          ) : isSoldOut ? (
            'Sold out'
          ) : (
            'Checkout'
          )}
        </button>
      )
    }

    return (
      <button
        type="button"
        className="package-purchase__cta"
        onClick={showSeatmap}
      >
        Choose your seats
      </button>
    )
  }

  const cssVars = {
    '--pkg-primary': primaryColor,
    '--pkg-primary-dark': adjustHex(primaryColor, -28),
    '--pkg-primary-light': adjustHex(primaryColor, 24),
  }

  const marketplaceLabel = organization?.name
    ? `Official ticketing marketplace for ${organization.name}`
    : 'Official ticketing marketplace'

  return (
    <div className="package-purchase" style={cssVars}>
      <div ref={heroRef} className="package-purchase__hero">
        <div className="package-purchase__hero-blob" aria-hidden />
        <div className="package-purchase__hero-inner">
          <div className="package-purchase__hero-top">
            <button
              type="button"
              className="package-purchase__back"
              aria-label="Back"
              onClick={handleGoBack}
            >
              <BackIcon />
            </button>
            <div className="package-purchase__eyebrow">
              Season package · {seasonLabel}
            </div>
          </div>

          <div className="package-purchase__hero-main">
            <div className="package-purchase__logo-tile">
              {logoSrc ? (
                <img src={logoSrc} alt={organization?.name || 'Organization'} />
              ) : (
                <span className="package-purchase__logo-fallback">
                  {organization?.name?.charAt(0) || 'B'}
                </span>
              )}
            </div>
            <div className="package-purchase__hero-copy">
              <h1 className="package-purchase__title">{eventPackage?.name}</h1>
              <div className="package-purchase__badges">
                {badges.map((badge) => (
                  <span key={badge} className="package-purchase__badge">
                    {badge}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`package-purchase__body${
          mobile ? ' package-purchase__body--mobile' : ''
        }`}
      >
        <div className="package-purchase__games">
          <div className="package-purchase__games-header">
            <div className="package-purchase__games-label">
              {gameCount} {getSinglarOrPluralWord(gameCount || 1, 'Game')}{' '}
              Included
            </div>
            {dateRange && (
              <div className="package-purchase__games-range">{dateRange}</div>
            )}
          </div>

          <div className="package-purchase__games-list">
            {gameRows.length === 0 && (
              <div className="package-purchase__empty">
                No games are linked to this package yet.
              </div>
            )}
            {gameRows.map((game) => (
              <div key={game.key} className="package-purchase__game-row">
                <div className="package-purchase__game-date">
                  <div className="package-purchase__game-mon">{game.mon}</div>
                  <div className="package-purchase__game-day">{game.day}</div>
                </div>
                <div className="package-purchase__game-info">
                  <div className="package-purchase__game-title">
                    {game.title}
                  </div>
                  {game.sub && (
                    <div className="package-purchase__game-sub">{game.sub}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {!mobile && (
          <aside className="package-purchase__aside">
            <div
              className="package-purchase__card"
              style={{
                top: heroH + 16,
                maxHeight: `calc(100vh - ${heroH + 40}px)`,
              }}
            >
              <div className="package-purchase__card-price">
                <span className="package-purchase__card-label">
                  Season tickets
                </span>
                <span className="package-purchase__card-amount">
                  {priceLabel}
                </span>
                {addedFees.length === 0 ? (
                  <span className="package-purchase__card-note">
                    Taxes and fees included
                  </span>
                ) : (
                  addedFees.map((line) => (
                    <span className="package-purchase__card-note" key={line.id}>
                      + {formatCurrency(line.amount)} {line.name}
                    </span>
                  ))
                )}
              </div>

              {gaOnly && !isSoldOut && (
                <Form.Select
                  aria-label="Ticket quantity"
                  className="package-purchase__qty"
                  value={selectedQuantity || ''}
                  disabled={checkingAvailability}
                  onChange={(e) => setQuantity(Number(e.target.value))}
                >
                  {quantityOptions.map((option) => (
                    <option value={option} key={option}>
                      {option} {getSinglarOrPluralWord(option, 'Ticket')}
                    </option>
                  ))}
                </Form.Select>
              )}

              {primaryCta()}

              <div className="package-purchase__card-footer">
                Delivered to your wallet. Transfer any single game you
                can&apos;t make.
              </div>
            </div>
          </aside>
        )}
      </div>

      {mobile && (
        <div className="package-purchase__mobile-bar">
          <div className="package-purchase__mobile-price">
            <span className="package-purchase__mobile-amount">
              {priceLabel}
            </span>
            {addedFees.length === 0 ? (
              <span className="package-purchase__mobile-note">
                Taxes &amp; fees included
              </span>
            ) : (
              addedFees.map((line) => (
                <span className="package-purchase__mobile-note" key={line.id}>
                  + {formatCurrency(line.amount)} {line.name}
                </span>
              ))
            )}
          </div>
          {gaOnly && !isSoldOut && (
            <Form.Select
              aria-label="Ticket quantity"
              className="package-purchase__qty package-purchase__qty--mobile"
              value={selectedQuantity || ''}
              disabled={checkingAvailability}
              onChange={(e) => setQuantity(Number(e.target.value))}
            >
              {quantityOptions.map((option) => (
                <option value={option} key={option}>
                  {option}
                </option>
              ))}
            </Form.Select>
          )}
          {primaryCta()}
        </div>
      )}

      {!mobile && (
        <div className="package-purchase__footer-pill">
          <img src={logoWhite} alt="Blocktickets" />
          <span>{marketplaceLabel}</span>
        </div>
      )}
    </div>
  )
}
